import { Grid } from '@mui/material'
import { useEffect } from 'react'
import { connect } from 'react-redux'
import ClientAmmunitionActions from '@ClientActions/ammunition.action'
import * as Redux from 'redux'
import { ICommonQuery } from '@Models/common.model'
import { Controller, useForm } from 'react-hook-form'
import { FormService } from '@Services/form.service'
import BaseSelect from '@Components/Form/BaseSelect/BaseSelect'
import ClassificationAction from '@ClientActions/classification.action'
import { MClassification } from '@Models/classification.model'
import moment from 'moment'
import { EClassifications } from '@Enum/classifications.enum'
import { APIServices } from '@Services/api.service'
import BaseDatePicker from '@Components/Form/BaseDatePicker/BaseDatePicker'
import { RedditTextField } from '@Components/Form/RedditTextField/RedditTextField'
import CustomModal from '@Components/Modal/CustomModal/CustomModal'

interface IAddAmmunitionModal {
    open: boolean
    onClose: () => void

    // redux
    addAmmunition?: (body: any) => Promise<any>
    getMakes?: () => void
    makes?: MClassification[]
}

function AddAmmunitionModal(props: IAddAmmunitionModal) {
    // core section
    const { open, onClose, addAmmunition, getMakes, makes } = props
    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm()
    const classesInput = 'reddit-input w-100'

    // hooks section
    useEffect(() => {
        if (getMakes) getMakes()
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    // functions section
    /**
     * handle form submit and create new Ammunition
     * @param formData
     */
    const onSubmit = (formData: any) => {
        const { make, madeAt, caliber, qmicrostamp, fflId } = formData
        const type = 'AMMUNITION'
        const body = {
            make,
            madeAt: moment(madeAt).toISOString(),
            caliber,
            qmicrostamp,
            fflId,
            type,
        }
        if (addAmmunition)
            addAmmunition(body).then(() => {
                onClose()
            })
    }

    // main section
    return (
        <CustomModal
            modalTitle="Add Ammunition"
            open={open}
            onClose={() => onClose()}
        >
            <form>
                <div className="modal-body p-0">
                    <Grid container spacing={2}>
                        <Grid item sm={6} xs={12}>
                            <Controller
                                control={control}
                                name="make"
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <BaseSelect
                                        {...field}
                                        label="Make"
                                        className="mb-2"
                                        value={value}
                                        onChange={onChange}
                                        options={makes}
                                        fullWidth
                                        error={errors?.make && true}
                                        helperText={FormService.getErrorMessage(
                                            errors,
                                            `make`,
                                            'Make'
                                        )}
                                        inputRef={ref}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                            <Controller
                                control={control}
                                name="madeAt"
                                defaultValue={null}
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange },
                                }: any) => (
                                    <BaseDatePicker
                                        label="Date"
                                        onChange={onChange}
                                        value={value}
                                        fullWidth
                                        inputRef={ref}
                                        error={errors?.madeAt && true}
                                        helperText={FormService.getErrorMessage(
                                            errors,
                                            'madeAt',
                                            'Date'
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item sm={6} xs={12} className="pt-2">
                            <Controller
                                control={control}
                                name="caliber"
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <RedditTextField
                                        {...field}
                                        value={value}
                                        onChange={onChange}
                                        inputRef={ref}
                                        label="Caliber"
                                        variant="filled"
                                        className={classesInput}
                                        error={errors?.caliber && true}
                                        helperText={FormService.getErrorMessage(
                                            errors,
                                            'caliber',
                                            'Caliber'
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item sm={6} xs={12} className="pt-2">
                            <Controller
                                control={control}
                                name="qmicrostamp"
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <RedditTextField
                                        {...field}
                                        value={value}
                                        onChange={onChange}
                                        inputRef={ref}
                                        label="Micro Stamp #"
                                        variant="filled"
                                        className={classesInput}
                                        error={errors?.qmicrostamp && true}
                                        helperText={FormService.getErrorMessage(
                                            errors,
                                            'qmicrostamp',
                                            'Micro Stamp #'
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item sm={6} xs={12} className="pt-2">
                            <Controller
                                control={control}
                                name="fflId"
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <RedditTextField
                                        {...field}
                                        value={value}
                                        onChange={onChange}
                                        inputRef={ref}
                                        label="FFL #"
                                        variant="filled"
                                        className={classesInput}
                                        error={errors?.fflId && true}
                                        helperText={FormService.getErrorMessage(
                                            errors,
                                            'fflId',
                                            'FFL #'
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </div>
                <div className="modal-footer">
                    <span
                        className="modal-action"
                        onClick={handleSubmit(onSubmit)}
                    >
                        OK
                    </span>
                </div>
            </form>
        </CustomModal>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { ammunition } = store?.client?.ammunition
    const { makes } = store?.client.classifications
    return {
        ammunition,
        makes,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const { getAmmunition, addAmmunition } = ClientAmmunitionActions
    const { getClassifications } = ClassificationAction
    return {
        getMakes: () =>
            dispatch(getClassifications(EClassifications.MAKE) as any),
        getAmmunition: (params?: ICommonQuery) =>
            dispatch(getAmmunition(params) as any),
        addAmmunition: (body: any) => dispatch(addAmmunition(body) as any),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AddAmmunitionModal)
